

import React from 'react';
import { CheckCircle2, Clock, AlertCircle, ChevronRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { daysBetween, shortDay, plural, type DashboardTask } from '../../useDashboard';

interface TasksWidgetProps {
  today: string;
  tasks: DashboardTask[];
  openCount: number;
  overdueCount: number;
}

const priorityColors: Record<string, string> = {
  high: 'text-red-700 bg-red-50',
  medium: 'text-amber-700 bg-amber-50',
  low: 'text-stone-600 bg-stone-100',
};

function dueText(task: DashboardTask, today: string): { text: string; tone: string } {
  if (!task.due_date) return { text: 'No due date', tone: 'text-stone-400' };
  const offset = daysBetween(today, task.due_date);
  if (offset < 0) return { text: `Overdue · was due ${shortDay(task.due_date)}`, tone: 'text-red-700 font-semibold' };
  if (offset === 0) return { text: 'Due today', tone: 'text-amber-700 font-semibold' };
  if (offset === 1) return { text: 'Due tomorrow', tone: 'text-stone-600' };
  return { text: `Due ${shortDay(task.due_date)}`, tone: 'text-stone-500' };
}

export function TasksWidget({ today, tasks, openCount, overdueCount }: TasksWidgetProps) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-stone-800">Open tasks</h3>
          <p className="text-xs text-stone-500">
            {plural(openCount, 'task')} to do{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
          </p>
        </div>
        <Link to="/tasks" className="inline-flex items-center h-10 px-2 -mr-2 text-sm text-amber-700 hover:text-amber-800 font-medium">
          All tasks
        </Link>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
          <p className="text-sm text-stone-600 font-medium">Nothing left to do</p>
          <Link to="/tasks?new=1" className="mt-2 inline-flex items-center gap-1 h-10 px-3 text-sm font-semibold text-amber-700 hover:underline">
            <Plus className="w-4 h-4" /> New task
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {tasks.map((task) => {
            const due = dueText(task, today);
            const Icon = task.overdue ? AlertCircle : Clock;
            return (
              <Link
                key={task.id}
                to="/tasks"
                className="flex items-start gap-2.5 min-h-[56px] p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-colors group"
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${task.overdue ? 'text-red-600' : 'text-amber-500'}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-stone-800 truncate group-hover:text-amber-700">{task.title}</p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                    <span className={`text-xs ${due.tone}`}>{due.text}</span>
                    {task.priority && (
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${priorityColors[task.priority] || 'bg-stone-100 text-stone-600'}`}>
                        {task.priority}
                      </span>
                    )}
                  </div>
                  {(task.event || task.assigned_to) && (
                    <p className="text-[11px] text-stone-500 truncate mt-0.5">
                      {[task.event?.name, task.assigned_to ? `for ${task.assigned_to}` : ''].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 mt-0.5 text-stone-400 group-hover:text-stone-600 flex-shrink-0" />
              </Link>
            );
          })}
          {openCount > tasks.length && (
            <Link to="/tasks" className="flex items-center justify-center gap-1 h-10 rounded-lg border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50">
              See all {openCount} open tasks <ChevronRight className="w-4 h-4 text-stone-400" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
